import { z } from 'zod';

/**
 * Create Project Request DTO
 * 
 * Validates client input for DDD project creation
 */
export const CreateProjectRequestSchema = z.object({
    name: z.string()
        .min(2, 'Project name must be at least 2 characters')
        .max(100, 'Project name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9\s-]*$/, 'Project name must start with a letter and contain only letters, numbers, spaces, and hyphens'),
    description: z.string()
        .min(20, 'Description must be at least 20 characters')
        .max(2000, 'Description must be less than 2000 characters'),
    vision: z.string()
        .min(50, 'Vision must be at least 50 characters')
        .max(1000, 'Vision must be less than 1000 characters'),
    team: z.object({
        lead: z.string().max(100).optional(),
        domainExperts: z.array(z.string().max(100)).max(10).optional(),
        developers: z.array(z.string().max(100)).max(20).optional(),
        stakeholders: z.array(z.string().max(100)).max(20).optional()
    }).optional(),
    scope: z.object({
        businessArea: z.string().min(5).max(200),
        objectives: z.array(z.string().min(10).max(500)).min(1).max(10),
        constraints: z.array(z.string().min(5).max(300)).max(10),
        assumptions: z.array(z.string().min(5).max(300)).max(10)
    }),
    timeline: z.object({
        startDate: z.string().datetime('Invalid start date format'),
        targetDate: z.string().datetime('Invalid target date format').optional(),
        milestones: z.array(z.object({
            name: z.string().min(5).max(100),
            targetDate: z.string().datetime('Invalid milestone date format'),
            description: z.string().max(500).optional()
        })).max(20).optional()
    }),
    risks: z.array(z.object({
        risk: z.string().min(10).max(500),
        impact: z.enum(['low', 'medium', 'high', 'critical']),
        probability: z.enum(['low', 'medium', 'high']),
        mitigation: z.string().min(10).max(1000)
    })).max(20).optional()
});

export type CreateProjectRequest = z.infer<typeof CreateProjectRequestSchema>;

/**
 * Update Project Request DTO
 */
export const UpdateProjectRequestSchema = z.object({
    name: z.string()
        .min(2, 'Project name must be at least 2 characters')
        .max(100, 'Project name must be less than 100 characters')
        .regex(/^[a-zA-Z][a-zA-Z0-9\s-]*$/, 'Project name must start with a letter and contain only letters, numbers, spaces, and hyphens')
        .optional(),
    description: z.string()
        .min(20, 'Description must be at least 20 characters')
        .max(2000, 'Description must be less than 2000 characters')
        .optional(),
    vision: z.string()
        .min(50, 'Vision must be at least 50 characters')
        .max(1000, 'Vision must be less than 1000 characters')
        .optional(),
    status: z.enum(['planning', 'discovery', 'modeling', 'implementation', 'maintenance', 'archived']).optional(),
    team: z.object({
        lead: z.string().max(100).optional(),
        domainExperts: z.array(z.string().max(100)).max(10).optional(),
        developers: z.array(z.string().max(100)).max(20).optional(),
        stakeholders: z.array(z.string().max(100)).max(20).optional()
    }).optional(),
    scope: z.object({
        businessArea: z.string().min(5).max(200),
        objectives: z.array(z.string().min(10).max(500)).min(1).max(10),
        constraints: z.array(z.string().min(5).max(300)).max(10),
        assumptions: z.array(z.string().min(5).max(300)).max(10)
    }).optional(),
    timeline: z.object({
        startDate: z.string().datetime('Invalid start date format'),
        targetDate: z.string().datetime('Invalid target date format').optional(),
        milestones: z.array(z.object({
            name: z.string().min(5).max(100),
            targetDate: z.string().datetime('Invalid milestone date format'),
            description: z.string().max(500).optional()
        })).max(20).optional()
    }).optional(),
    risks: z.array(z.object({
        risk: z.string().min(10).max(500),
        impact: z.enum(['low', 'medium', 'high', 'critical']),
        probability: z.enum(['low', 'medium', 'high']),
        mitigation: z.string().min(10).max(1000)
    })).max(20).optional()
});

export type UpdateProjectRequest = z.infer<typeof UpdateProjectRequestSchema>;

/**
 * Get Projects Query Parameters DTO
 */
export const GetProjectsQuerySchema = z.object({
    status: z.enum(['planning', 'discovery', 'modeling', 'implementation', 'maintenance', 'archived', 'all']).default('modeling'),
    health: z.enum(['excellent', 'good', 'concerning', 'critical', 'all']).default('all'),
    team: z.string().max(100).optional(), // Filter by team member
    limit: z.coerce.number().min(1).max(100).default(20),
    offset: z.coerce.number().min(0).default(0),
    search: z.string().max(100).optional(),
    includeProgress: z.coerce.boolean().default(true),
    includeTeam: z.coerce.boolean().default(false)
});

export type GetProjectsQuery = z.infer<typeof GetProjectsQuerySchema>;

/**
 * Project Parameters DTO
 */
export const ProjectParamsSchema = z.object({
    projectId: z.string().uuid('Invalid project ID format')
});

export type ProjectParams = z.infer<typeof ProjectParamsSchema>;